import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, Network, Wallet, GraduationCap, ShieldCheck, User, Copy, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const CERT_ICON = { CERTIFIED: "✓", "AWAITING CERTIFICATION": "◐", "ASSESSMENT PENDING": "◐", "TRAINING IN PROGRESS": "◐", "TRAINING REQUIRED": "○" };

export default function ApplicatorDashboard() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [ref, setRef] = useState("");

  const load = () => {
    api.get("/applicator/dashboard").then(({ data }) => setD(data)).catch(() => {});
    api.get("/applicator/me").then(({ data }) => setRef(data.referral_link)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  if (!d) return <div className="text-gray-500" data-testid="applicator-dashboard-loading">Loading…</div>;
  const a = d.applicator;

  const toggleAvail = async () => {
    const next = a.availability === "online" ? "offline" : "online";
    await api.put("/applicator/availability", { availability: next }); load();
  };
  const inr = (n) => `₹${(n || 0).toLocaleString()}`;

  const cards = [
    [Briefcase, t("jobs"), "/applicator/jobs"], [GraduationCap, t("training"), "/applicator/training"],
    [ShieldCheck, t("certification"), "/applicator/certification"], [Network, t("network"), "/applicator/network"],
    [Wallet, t("earnings"), "/applicator/earnings"], [User, t("profile"), "/applicator/profile"],
  ];

  return (
    <div data-testid="applicator-dashboard-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="errah-label gold-text mb-1">{t("good_morning")}</p>
          <h1 className="serif-head text-3xl lg:text-4xl" data-testid="dashboard-name">{d.greeting_name}</h1>
          <p className="text-sm text-gray-400 mt-1">{a.applicator_code} · {a.level} · {a.status}</p>
        </div>
        <button onClick={toggleAvail} data-testid="availability-toggle"
          className={`px-4 py-2 text-xs font-semibold tracking-wide border ${a.availability === "online" ? "border-emerald-500 text-emerald-400" : "border-white/20 text-gray-400"}`}>
          {a.availability === "online" ? "● " + t("online") : "○ " + t("offline")}
        </button>
      </div>

      {a.status !== "approved" && (
        <div className="mt-5 hair-border bg-[#C9A45C]/10 border-[#C9A45C]/40 p-4 text-sm text-[#E4C987]" data-testid="pending-banner">
          Your application is <b>{a.status}</b>. You'll receive certified jobs once ERRĀH approves and certifies your services.
        </div>
      )}

      {/* job counts */}
      <div className="mt-6 grid grid-cols-3 gap-3" data-testid="dashboard-jobcounts">
        {[[t("new_jobs"), d.jobs.new, "/applicator/jobs"], [t("active_jobs"), d.jobs.active, "/applicator/jobs"], [t("completed_jobs"), d.jobs.completed, "/applicator/jobs"]].map(([l, v, to]) => (
          <button key={l} onClick={() => navigate(to)} className="hair-border bg-white/[0.02] p-5 text-left hover:bg-white/[0.04] transition-colors"><p className="errah-label mb-1">{l}</p><p className="font-display text-2xl gold-text">{v}</p></button>
        ))}
      </div>

      {/* monthly earnings */}
      <div className="mt-4 hair-border bg-white/[0.02] p-5" data-testid="dashboard-monthly">
        <p className="errah-label gold-text mb-3">{t("this_month")}</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[[t("service_revenue"), d.monthly.service], [t("installation_earnings"), d.monthly.installation], [t("referral_earnings"), d.monthly.referral], [t("network_earnings"), d.monthly.network], [t("total_eligible"), d.monthly.total]].map(([l, v]) => (
            <div key={l} className="border border-white/10 p-3"><p className="text-[10px] text-gray-500 uppercase leading-tight">{l}</p><p className="font-display text-lg mt-1">{inr(v)}</p></div>
          ))}
        </div>
      </div>

      <div className="mt-4 grid md:grid-cols-2 gap-4">
        {/* certifications */}
        <div className="hair-border bg-white/[0.02] p-5" data-testid="dashboard-certs">
          <div className="flex items-center justify-between mb-3"><p className="errah-label gold-text">{t("certifications")}</p><button onClick={() => navigate("/applicator/certification")} className="text-xs text-gray-400 hover:text-[#C9A45C]">→</button></div>
          {(d.certifications || []).length ? (
            <div className="space-y-1.5 max-h-40 overflow-y-auto">{d.certifications.slice(0, 8).map((c) => (
              <div key={c.service} className="flex items-center justify-between text-sm"><span className="text-gray-300">{c.service}</span><span className={c.status === "CERTIFIED" ? "text-emerald-400" : "text-[#E4C987]"}>{CERT_ICON[c.status] || "○"} {c.status}</span></div>
            ))}</div>
          ) : <p className="text-xs text-gray-600">No services yet — start training.</p>}
        </div>

        {/* training + network + rating */}
        <div className="space-y-4">
          <div className="hair-border bg-white/[0.02] p-5" data-testid="dashboard-training">
            <p className="errah-label gold-text mb-2">{t("training")}</p>
            <div className="flex gap-6 text-sm"><span>{t("courses_in_progress")}: <b className="gold-text">{d.training.in_progress}</b></span><span>{t("assessments_pending")}: <b className="gold-text">{d.training.assessments_pending}</b></span></div>
          </div>
          <div className="hair-border bg-white/[0.02] p-5" data-testid="dashboard-network">
            <p className="errah-label gold-text mb-2">{t("network")}</p>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div><p className="text-[10px] text-gray-500 uppercase">{t("active_applicators")}</p><p className="font-display">{d.network.active_applicators}</p></div>
              <div><p className="text-[10px] text-gray-500 uppercase">{t("network_earnings")}</p><p className="font-display">{inr(d.network.eligible_network_earnings)}</p></div>
              <div><p className="text-[10px] text-gray-500 uppercase">{t("overall_rating")}</p><p className="font-display inline-flex items-center gap-1"><Star size={13} className="text-[#C9A45C] fill-[#C9A45C]" /> {d.ratings?.overall ?? "—"}</p></div>
            </div>
          </div>
        </div>
      </div>

      {/* referral */}
      <div className="mt-4 hair-border bg-white/[0.02] p-5 flex flex-wrap items-center justify-between gap-3" data-testid="referral-box">
        <div><p className="errah-label mb-1">{t("referral_code")}</p><p className="font-display text-lg gold-text">{a.applicator_code}</p></div>
        <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}${ref}`); toast.success("Referral link copied"); }}
          className="inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 text-sm hover:bg-white hover:text-black transition-colors"><Copy size={15} /> {t("referral_link")}</button>
      </div>

      {/* quick links */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="dashboard-cards">
        {cards.map(([Icon, label, to]) => (
          <button key={label} onClick={() => navigate(to)} className="hair-border bg-white/[0.02] p-5 text-left hover:bg-white/[0.05] transition-colors">
            <Icon className="text-[#C9A45C]" size={22} /><p className="mt-3 font-display text-sm">{label}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
